import { render } from 'solid-js/web';
import { HeroImageGallerySwiper } from '../containers/HeroImageGallerySwiper';
import { HeroFormContext, HeroFormProvider, createExperimentGetters, getColorData, useHeroForm } from '../contexts/HeroFormContext';
import { GalleryPdpHeadline } from '../containers/GalleryPdpHeadline';

// The PDP hero is two Solid roots inside one flex container:
//   1. gallery root  - mobile headline + image gallery. Mounts as soon as the fold-stage data
//                      globals exist (window.__heroData) and the server-rendered LCP image has painted.
//   2. details root  - HeroProductDetails under the full HeroFormProvider (measurement, size, style
//                      and accent forms). Mounts once the form-stage globals exist (window.__heroFormData)
//                      and its chunk has arrived; until then the Liquid-rendered headline block
//                      (#hero-details-ssr, snippets/hero-details-ssr.liquid) stands in for it.
// Both roots share one colour signal (HeroFormContext.getColorData), so a swatch tap in either
// column drives the gallery, and the form root adopts whatever colour was picked before it mounted.

// Start downloading the details chunk as soon as this entry executes, not when the root mounts.
const heroProductDetailsChunk = import('../containers/HeroProductDetails');

const SSR_DETAILS_ID = 'hero-details-ssr';
const SSR_COLOR_EVENT = 'hero-ssr:color';

// Mirrors the right column of snippets/product-hero-skeleton.liquid ('old' section version, which
// has no server-rendered headline block).
const HeroDetailsPlaceholder = () => (
  <div class="mx-auto flex flex-1 flex-col items-center gap-2 px-2 py-2 sm:col-span-1" aria-hidden="true">
    <div class="my-3 flex w-full flex-col items-center gap-2">
      <div class="bg-secondary-300 h-5 w-16 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-5 w-20 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-5 w-14 animate-pulse rounded-full"></div>
    </div>
    <div class="my-3 flex w-full justify-center gap-1">
      <div class="bg-secondary-300 h-5 w-5 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-5 w-5 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-5 w-5 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-5 w-5 animate-pulse rounded-full"></div>
    </div>
    <div class="my-4 flex flex-col gap-2">
      <div class="bg-secondary-300 h-8 w-32 animate-pulse rounded-full"></div>
      <div class="bg-secondary-300 h-8 w-32 animate-pulse rounded-full"></div>
    </div>
  </div>
);

const hideHeroSkeleton = () => {
  const skeleton = document.getElementById('product-hero-skeleton');
  skeleton.classList.add('hidden');
  skeleton.style.display = 'none'; // the skeleton's responsive display classes outrank `.hidden`
}

// The skeleton carries a server-rendered copy of the first gallery image (#hero-lcp-img, see
// snippets/hero-lcp-image.liquid) so the browser can fetch and paint the LCP long before this
// bundle runs. If we mount while that image is still in flight, the skeleton is hidden before it
// ever paints and the LCP silently becomes the Solid-rendered <img> — which only paints after
// the whole bundle has executed. Wait for the image (bounded), then give the compositor a frame.
const LCP_IMAGE_WAIT_MS = 3000;

const whenHeroLcpImagePainted = () => new Promise((resolve) => {
  const img = document.getElementById('hero-lcp-img');
  if (!img || !img.getAttribute('src')) {
    resolve();
    return;
  }

  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    // two frames: one to lay out/paint the decoded image, one to be sure it was committed
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  };

  if (img.complete) {
    settle();
    return;
  }
  img.addEventListener('load', settle, { once: true });
  img.addEventListener('error', settle, { once: true });
  setTimeout(settle, LCP_IMAGE_WAIT_MS);
});

// Slim context for the gallery root: only what HeroImageGallerySwiper / GalleryPdpHeadline read.
// Same HeroFormContext object, so those components work unchanged under either provider.
const HeroGalleryProvider = (props) => {
  const color = getColorData(colorInitialObj);

  // swatch taps in the server-rendered headline block, before the details root exists
  const onSsrColor = (event) => {
    const selected = event.detail?.color;
    if (selected && color.color().options[selected]) color.handlers.handleColorSelection(selected);
  };
  window.addEventListener(SSR_COLOR_EVENT, onSsrColor);

  const value = {
    productInfo,
    heroSectionSettings,
    reviewProfilePictures: typeof reviewProfilePictures !== 'undefined' ? reviewProfilePictures : [],
    color,
    getters: createExperimentGetters(),
  };

  return (
    <HeroFormContext.Provider value={value}>
      {props.children}
    </HeroFormContext.Provider>
  )
}

const HeroDetailsRoot = (props) => (
  <HeroFormProvider>
    <props.component />
  </HeroFormProvider>
);

const mountHero = () => {
  const host = document.getElementById('product-hero-flat');
  // Adopt the Liquid-rendered headline block: it moves into the gallery root's container and stays
  // on screen (still handling swatch taps) until the details root replaces it.
  const standIn = document.getElementById(SSR_DETAILS_ID) ?? HeroDetailsPlaceholder();
  let container;

  hideHeroSkeleton();

  render(() => (
    <HeroGalleryProvider>
      <div ref={container} class="flex flex-col sm:landscape:flex-row sm:landscape:justify-center">
        <div class="sm:hidden">
          <GalleryPdpHeadline context={useHeroForm} />
        </div>
        <HeroImageGallerySwiper context={useHeroForm} />
        {standIn}
      </div>
    </HeroGalleryProvider>
  ), host);

  const formDataReady = window.__heroFormData ?? Promise.resolve();
  Promise.all([formDataReady, heroProductDetailsChunk]).then(([, module]) => {
    // replace the stand-in in place: render() appends, so the details column lands after the gallery
    standIn.remove();
    render(() => <HeroDetailsRoot component={module.HeroProductDetails} />, container);
  }).catch((error) => {
    console.error('[hero] details root failed to mount; headline block left server-rendered', error);
  });
};

whenHeroLcpImagePainted().then(mountHero);
